/**
 * Générateur — Planifier une enquête et recueillir des données
 * Attendu ID : 675 | Niveau : 6ème | Domaine : Organisation et gestion de données
 *
 * Types générés :
 *   Template (enquete_tableau_double_entree) : qcm_unique, texte, relier, tableau_vf, texte_trou_drag
 *
 * Labels de référence :
 *   - Lire et compléter un tableau à double entrée
 *   - Identifier population et caractère d'une enquête
 *   - Associer les termes statistiques à leur définition
 *   - Vérifier des affirmations sur un tableau de données
 *   - Compléter un texte sur la méthodologie d'enquête
 *
 * Variantes estimées : 500+ (objectif : 20/20 uniques sur 20 itérations auditGen)
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 675;
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

const CONTEXTS = [
    {
        theme: 'les sports préférés',
        rows: ['Football', 'Basketball', 'Tennis', 'Natation'],
        cols: ['Filles', 'Garçons'],
        population: 'les élèves de 6ᵉ',
        caractere: 'le sport préféré',
    },
    {
        theme: 'les moyens de transport pour venir au collège',
        rows: ['Vélo', 'Bus', 'Voiture', 'Marche'],
        cols: ['6ᵉ', '5ᵉ'],
        population: 'les élèves de 6ᵉ et 5ᵉ',
        caractere: 'le moyen de transport',
    },
    {
        theme: 'les animaux de compagnie',
        rows: ['Chien', 'Chat', 'Hamster', 'Poisson'],
        cols: ['Filles', 'Garçons'],
        population: 'les élèves de 6ᵉ',
        caractere: "l'animal de compagnie",
    },
    {
        theme: 'les fruits préférés',
        rows: ['Pomme', 'Banane', 'Orange', 'Fraise'],
        cols: ['CM2', '6ᵉ'],
        population: 'les élèves de CM2 et 6ᵉ',
        caractere: 'le fruit préféré',
    },
    {
        theme: 'les matières préférées',
        rows: ['Maths', 'Français', 'Histoire', 'SVT'],
        cols: ['Filles', 'Garçons'],
        population: 'les élèves de 6ᵉ',
        caractere: 'la matière préférée',
    },
    {
        theme: 'les genres de livres préférés',
        rows: ['Roman', 'BD', 'Manga', 'Documentaire'],
        cols: ['6ᵉ A', '6ᵉ B'],
        population: 'les élèves de 6ᵉ',
        caractere: 'le genre de livre préféré',
    },
    {
        theme: 'les langues étudiées',
        rows: ['Anglais', 'Espagnol', 'Allemand', 'Italien'],
        cols: ['6ᵉ', '5ᵉ'],
        population: 'les élèves de 6ᵉ et 5ᵉ',
        caractere: 'la langue étudiée',
    },
    {
        // Colonnes = moments, pas des groupes d'élèves (revue 2026-08-06) :
        // enquête de CONSOMMATION, avec gabarit de phrase dédié — le gabarit
        // générique produisait « le nombre de Crêpe chez les Matin ».
        theme: 'les goûters consommés au collège',
        rows: ['Crêpe', 'Gaufre', 'Cookie', 'Muffin'],
        cols: ['Matin', 'Après-midi'],
        population: 'les élèves de 6ᵉ',
        caractere: 'le goûter consommé',
        cellule: (r, c) => {
            const pluriels = ['crêpes consommées', 'gaufres consommées', 'cookies consommés', 'muffins consommés'];
            const moments = ['le matin', "l'après-midi"];
            return `${pluriels[r]} ${moments[c]}`;
        },
    },
];

// Fragment décrivant une cellule. Forme explicite « élèves ayant répondu X
// chez les Y » (revue 2026-08-06, décision utilisateur : pas de métonymie
// « le nombre de Chien ») ; les contextes à colonnes non-groupes portent leur
// propre `cellule()`.
const celluleTxt = (ctx, r, c) =>
    ctx.cellule ? ctx.cellule(r, c) : `élèves ayant répondu ${ctx.rows[r]} chez les ${ctx.cols[c]}`;

// « de » ou « d' » devant le fragment (« nombre d'élèves… », « de crêpes… »).
const deCelluleTxt = (ctx, r, c) => {
    const f = celluleTxt(ctx, r, c);
    return /^[aeiouyéèê]/i.test(f) ? `d'${f}` : `de ${f}`;
};

// « auprès des élèves de CM2 et 6ᵉ » — l'intro doit suivre la population du
// contexte (revue 2026-08-06) : le « auprès des élèves de 6ᵉ » codé en dur
// contredisait le tableau dans 39,5 % des tirages (colonnes CM2/6ᵉ ou 6ᵉ/5ᵉ).
const aupresDe = (ctx) => `auprès ${ctx.population.replace(/^les /, 'des ')}`;

const VOCAB_BASE = [
    { term: 'Population', def: "L'ensemble des individus étudiés" },
    { term: 'Caractère', def: 'La propriété observée' },
    { term: 'Effectif', def: "Le nombre d'individus ayant une modalité" },
    { term: 'Modalité', def: 'Une valeur possible du caractère' },
    // « Donnée », « Tri » et « Fréquence » retirés (revue 2026-08-07) : hors
    // programme 6ᵉ ou redondants — l'élève ne pouvait que tirer au hasard.
    { term: 'Enquête', def: 'La démarche de collecte de données' },
    { term: 'Tableau', def: "L'outil pour organiser les données" },
    { term: 'Ligne', def: 'Une rangée horizontale du tableau' },
    { term: 'Colonne', def: 'Une rangée verticale du tableau' },
    { term: 'Total', def: 'La somme de tous les effectifs' },
];

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || 'Lire et compléter un tableau à double entrée',
});

// ─── Helpers tableau ─────────────────────────────────────────

/**
 * Génère une grille aléatoire 4×2 avec totaux.
 * Retourne { ctx, cells[4][2], rowTotals[4], colTotals[2], grandTotal }
 */
const genTable = () => {
    const ctx = randomChoice(CONTEXTS);
    const nRows = ctx.rows.length; // 4
    const nCols = ctx.cols.length; // 2
    const cells = [];
    const rowTotals = [];
    const colTotals = new Array(nCols).fill(0);
    let grandTotal = 0;

    for (let r = 0; r < nRows; r++) {
        const row = [];
        let rowSum = 0;
        for (let c = 0; c < nCols; c++) {
            const v = randInt(2, 15);
            row.push(v);
            rowSum += v;
            colTotals[c] += v;
        }
        cells.push(row);
        rowTotals.push(rowSum);
        grandTotal += rowSum;
    }

    return { ctx, cells, rowTotals, colTotals, grandTotal };
};

/**
 * Construit le HTML d'un tableau à double entrée.
 * maskedRow/maskedCol : coordonnées de la cellule masquée (-1 = aucune)
 */
const buildTableHtml = (table, maskedRow = -1, maskedCol = -1) => {
    const { ctx, cells, rowTotals, colTotals, grandTotal } = table;
    let html = `<table border="1" cellpadding="5" cellspacing="0">`;

    // Header
    html += `<tr><th></th>`;
    for (const col of ctx.cols) html += `<th>${col}</th>`;
    html += `<th>Total</th></tr>`;

    // Data rows
    for (let r = 0; r < ctx.rows.length; r++) {
        html += `<tr><td><b>${ctx.rows[r]}</b></td>`;
        for (let c = 0; c < ctx.cols.length; c++) {
            const val = (r === maskedRow && c === maskedCol) ? '?' : cells[r][c];
            html += `<td>${val}</td>`;
        }
        const rowT = (maskedRow === r && maskedCol >= 0)
            ? rowTotals[r] // show the total even when a cell is masked
            : rowTotals[r];
        html += `<td>${rowT}</td></tr>`;
    }

    // Total row
    html += `<tr><td><b>Total</b></td>`;
    for (let c = 0; c < ctx.cols.length; c++) {
        html += `<td>${colTotals[c]}</td>`;
    }
    html += `<td>${grandTotal}</td></tr>`;
    html += `</table>`;
    return html;
};

// ─── Format : qcm_unique ────────────────────────────────────

// QCM — Lire une cellule du tableau (compétence : lecture tableau)
const genQcmTableau = () => {
    const table = genTable();
    const { ctx, cells, rowTotals, colTotals } = table;
    const mr = randInt(0, ctx.rows.length - 1);
    const mc = randInt(0, ctx.cols.length - 1);
    const correct = cells[mr][mc];

    const tableHtml = buildTableHtml(table, mr, mc);
    const texte = `<p>Voici les résultats d'une enquête sur ${ctx.theme} ${aupresDe(ctx)}.</p>${tableHtml}<p>Quel est le nombre ${deCelluleTxt(ctx, mr, mc)} ?</p>`;

    const distractorSet = new Set();
    distractorSet.add(String(correct));
    // Calibrage anti-stratégies aveugles (revue 2026-08-06). Le total de ligne
    // est toujours au-dessus de la bonne réponse → elle ne peut jamais être le
    // max. Sans branche « min », « éliminer les extrêmes » gagnait 50 % ; avec
    // un distracteur en dessous systématique, « cocher le plus petit » gagnait
    // 52,8 %. À 1/3 de tirages « la bonne réponse est le min », les deux
    // stratégies plafonnent à ~33 %.
    const candidates = Math.random() < 1 / 3
        ? [
            rowTotals[mr],
            colTotals[mc],
            correct + randInt(1, 3),
        ]
        : [
            rowTotals[mr],
            Math.max(1, correct - randInt(1, 3)),
            cells[mr][(mc + 1) % ctx.cols.length],
            correct + randInt(1, 3),
            colTotals[mc],
        ];
    const distractors = [];
    for (const d of candidates) {
        const s = String(d);
        if (!distractorSet.has(s) && d > 0) {
            distractorSet.add(s);
            distractors.push(String(d));
        }
        if (distractors.length >= 3) break;
    }
    while (distractors.length < 3) {
        const v = String(randInt(1, 30));
        if (!distractorSet.has(v)) {
            distractorSet.add(v);
            distractors.push(v);
        }
    }

    const choix = [String(correct), ...distractors];
    const payload = makeQcm(choix, String(correct), texte, opts('Lire et compléter un tableau à double entrée'));
    payload._meta.generatorKey = 'lecture_tableau_qcm_unique';
    return payload;
};

// QCM — Identifier population / caractère (compétence : vocabulaire)
const genQcmVocabulaire = () => {
    const table = genTable();
    const { ctx } = table;
    const tableHtml = buildTableHtml(table);

    const qType = randomChoice(['population', 'caractere']);
    let texte, correct, distractors;

    if (qType === 'population') {
        // Refonte (revue 2026-08-07) : la compétence est de distinguer
        // population / caractère / modalité — plus jamais d'autres populations
        // en distracteurs (l'intro disait « 6ᵉ » et la bonne réponse était
        // « 6ᵉ et 5ᵉ » dans 36 % des tirages : réponse cohérente comptée fausse).
        texte = `<p>Voici les résultats d'une enquête sur ${ctx.theme} ${aupresDe(ctx)}.</p>${tableHtml}<p>Quelle est la population étudiée dans cette enquête ?</p>`;
        correct = ctx.population;
        distractors = shuffle([ctx.caractere, randomChoice(ctx.rows), 'le nombre total de réponses']);
    } else {
        // Même refonte que la branche population (revue 2026-08-07) : les
        // caractères d'autres enquêtes étaient hors sujet, éliminables sans
        // aucune connaissance du vocabulaire.
        texte = `<p>Voici les résultats d'une enquête sur ${ctx.theme} ${aupresDe(ctx)}.</p>${tableHtml}<p>Quel est le caractère observé dans cette enquête ?</p>`;
        correct = ctx.caractere;
        distractors = shuffle([ctx.population, randomChoice(ctx.rows), 'le nombre total de réponses']);
    }

    const choixSet = new Set([correct]);
    const finalDistractors = [];
    for (const d of distractors) {
        if (!choixSet.has(d)) {
            choixSet.add(d);
            finalDistractors.push(d);
        }
        if (finalDistractors.length >= 3) break;
    }
    while (finalDistractors.length < 3) {
        const filler = randomChoice(['le nombre total de réponses', "l'âge des élèves", 'la taille des élèves']);
        if (!choixSet.has(filler)) {
            choixSet.add(filler);
            finalDistractors.push(filler);
        }
    }

    const choix = [correct, ...finalDistractors];
    const payload = makeQcm(choix, correct, texte, opts('Identifier population et caractère d\'une enquête'));
    payload._meta.generatorKey = 'vocabulaire_enquete_qcm_unique';
    return payload;
};

// ─── Format : texte (saisie) ────────────────────────────────

const genSaisie = () => {
    const table = genTable();
    const { ctx, cells } = table;
    const mr = randInt(0, ctx.rows.length - 1);
    const mc = randInt(0, ctx.cols.length - 1);
    const correct = cells[mr][mc];

    const tableHtml = buildTableHtml(table, mr, mc);
    const texte = `<p>Voici les résultats d'une enquête sur ${ctx.theme} ${aupresDe(ctx)}.</p>${tableHtml}<p>Calcule le nombre manquant (indiqué par « ? ») dans le tableau.</p>`;

    // Pas de strict (revue 2026-08-06) : on teste un calcul, la comparaison
    // par valeur suffit (8 = 8,0 accepté).
    const payload = makeSaisie(String(correct), texte, opts('Lire et compléter un tableau à double entrée'));
    payload._meta.generatorKey = 'lecture_tableau_texte';
    return payload;
};

// ─── Format : relier ────────────────────────────────────────

const genRelier = () => {
    // Use context-specific pairs to maximize variability
    const table = genTable();
    const { ctx, cells, rowTotals, grandTotal } = table;
    const r = randInt(0, ctx.rows.length - 1);
    const c = randInt(0, ctx.cols.length - 1);

    // Build a rich pool mixing generic vocab + context-specific pairs.
    // Termes explicites (revue 2026-08-07) : « Chien / Filles » (notation à
    // barre jamais enseignée, lisible comme une fraction) devient « Chien
    // chez les Filles » ; le contexte goûters compte des goûters, pas des
    // élèves.
    const unite = ctx.cellule ? 'goûters' : 'élèves';
    const fragCellule = celluleTxt(ctx, r, c);
    const paireCellule = ctx.cellule
        ? { term: fragCellule.charAt(0).toUpperCase() + fragCellule.slice(1), def: `${cells[r][c]} goûters` }
        : { term: `${ctx.rows[r]} chez les ${ctx.cols[c]}`, def: `${cells[r][c]} élèves` };
    const contextPairs = [
        { term: 'Population', def: ctx.population },
        { term: 'Caractère', def: ctx.caractere },
        { term: `Effectif de ${ctx.rows[r]}`, def: `${rowTotals[r]} ${unite} au total` },
        paireCellule,
        { term: 'Total général', def: `${grandTotal} ${unite}` },
    ];
    const genericPairs = shuffle([...VOCAB_BASE]).slice(0, 5);
    const allPairs = shuffle([...contextPairs, ...genericPairs.map(v => ({ term: v.term, target: v.def, source: v.term }))]);

    // Deduplicate by term
    const seen = new Set();
    const uniquePairs = [];
    for (const p of allPairs) {
        const key = p.term || p.source;
        if (!seen.has(key)) {
            seen.add(key);
            uniquePairs.push({ source: p.term || p.source, target: p.def || p.target });
        }
    }

    let selected = uniquePairs.slice(0, 4);
    // Au moins 1 paire contextuelle par tirage (revue 2026-08-07) : 14,3 % des
    // tirages étaient 4 définitions génériques — le tableau affiché ne servait
    // à rien.
    // Comparaison sur le couple (terme, cible) : « Population » existe aussi
    // dans le pool générique avec une définition — le terme seul ne suffit pas.
    const clesContextuelles = new Set(contextPairs.map(p => `${p.term}|${p.def}`));
    if (!selected.some(p => clesContextuelles.has(`${p.source}|${p.target}`))) {
        const dejaPris = new Set(selected.map(p => p.source));
        const candidates = contextPairs.filter(p => !dejaPris.has(p.term));
        const paireCtx = randomChoice(candidates.length ? candidates : contextPairs);
        selected = shuffle([
            ...selected.filter(p => p.source !== paireCtx.term).slice(0, 3),
            { source: paireCtx.term, target: paireCtx.def },
        ]);
    }
    const tableHtml = buildTableHtml(table);
    const texte = `<p>Voici une enquête sur ${ctx.theme}.</p>${tableHtml}<p>Relie chaque terme à ce qui lui correspond.</p>`;

    const payload = makeRelier(selected, texte, opts('Associer les termes statistiques à leur définition'));
    payload._meta.generatorKey = 'vocabulaire_enquete_relier';
    return payload;
};

// ─── Format : tableau_vf ────────────────────────────────────

const genVF = () => {
    const table = genTable();
    const { ctx, cells, rowTotals, colTotals, grandTotal } = table;
    const tableHtml = buildTableHtml(table);

    // Chaque affirmation porte une clé sémantique (revue 2026-08-06) :
    // cellule r,c / total ligne / total colonne / total général. La sélection
    // impose des clés toutes distinctes — avant, 31,9 % des tirages avaient
    // deux affirmations sur le même objet, l'une donnant l'autre.
    const vraiPool = [];
    const fauxPool = [];
    const usedStatements = new Set();

    const addUnique = (pool, text, key) => {
        if (!usedStatements.has(text)) {
            usedStatements.add(text);
            pool.push({ l: text, k: key });
        }
    };

    // Generate VRAI statements
    for (let i = 0; i < 8; i++) {
        const r = randInt(0, ctx.rows.length - 1);
        const c = randInt(0, ctx.cols.length - 1);
        addUnique(vraiPool, `Il y a ${cells[r][c]} ${celluleTxt(ctx, r, c)}.`, `c:${r},${c}`);
    }
    // Échantillon de totaux seulement (revue 2026-08-06) : tous les totaux en
    // vrai + peu en faux donnait 62 % de « Vrai » sur les lignes « total ».
    for (const r of [randInt(0, ctx.rows.length - 1), randInt(0, ctx.rows.length - 1)]) {
        addUnique(vraiPool, `Le total de la ligne ${ctx.rows[r]} est ${rowTotals[r]}.`, `tr:${r}`);
    }
    {
        const c = randInt(0, ctx.cols.length - 1);
        addUnique(vraiPool, `Le total de la colonne ${ctx.cols[c]} est ${colTotals[c]}.`, `tc:${c}`);
    }
    addUnique(vraiPool, `Le total général est ${grandTotal}.`, 'tg');

    // Generate FAUX statements
    for (let i = 0; i < 8; i++) {
        const r = randInt(0, ctx.rows.length - 1);
        const c = randInt(0, ctx.cols.length - 1);
        const wrongVal = cells[r][c] + randomChoice([-1, 1, 2, -2]);
        if (wrongVal > 0 && wrongVal !== cells[r][c]) {
            addUnique(fauxPool, `Il y a ${wrongVal} ${celluleTxt(ctx, r, c)}.`, `c:${r},${c}`);
        }
    }
    // Wrong cell reading (swap row)
    for (let i = 0; i < 4; i++) {
        const r1 = randInt(0, ctx.rows.length - 1);
        const r2 = (r1 + 1) % ctx.rows.length;
        const c = randInt(0, ctx.cols.length - 1);
        if (cells[r1][c] !== cells[r2][c]) {
            addUnique(fauxPool, `Il y a ${cells[r2][c]} ${celluleTxt(ctx, r1, c)}.`, `c:${r1},${c}`);
        }
    }
    addUnique(fauxPool, `Le total général est ${grandTotal + randInt(1, 5)}.`, 'tg');
    // Totaux de ligne/colonne faussés (revue 2026-08-06) : sans eux, une
    // affirmation « Le total des X est N » était vraie dans 100 % des cas —
    // point gratuit sur un quart des lignes servies.
    for (let i = 0; i < 3; i++) {
        const r = randInt(0, ctx.rows.length - 1);
        const faux = rowTotals[r] + randomChoice([-3, -2, -1, 1, 2, 3]);
        if (faux > 0 && faux !== rowTotals[r]) {
            addUnique(fauxPool, `Le total de la ligne ${ctx.rows[r]} est ${faux}.`, `tr:${r}`);
        }
    }
    for (let i = 0; i < 2; i++) {
        const c = randInt(0, ctx.cols.length - 1);
        const faux = colTotals[c] + randomChoice([-3, -2, -1, 1, 2, 3]);
        if (faux > 0 && faux !== colTotals[c]) {
            addUnique(fauxPool, `Le total de la colonne ${ctx.cols[c]} est ${faux}.`, `tc:${c}`);
        }
    }

    // Select items: min 2V + 2F, total 5, clés toutes distinctes (V et F confondus)
    const nbVrais = randInt(2, 3);
    const nbFaux = 5 - nbVrais;

    const seenKeys = new Set();
    const pick = (pool, n) => {
        const out = [];
        for (const e of shuffle(pool)) {
            if (out.length >= n) break;
            if (!seenKeys.has(e.k)) {
                seenKeys.add(e.k);
                out.push(e.l);
            }
        }
        return out;
    };
    const selectedVrais = pick(vraiPool, nbVrais);
    const selectedFaux = pick(fauxPool, nbFaux);

    if (selectedVrais.length < nbVrais || selectedFaux.length < nbFaux) {
        // Fallback: regenerate
        return genVF();
    }

    const mixed = shuffle([
        ...selectedVrais.map(l => ({ l, r: 0 })),
        ...selectedFaux.map(l => ({ l, r: 1 })),
    ]);

    const lignes = mixed.map(m => m.l);
    const bonnesReponses = mixed.map(m => m.r);

    const texte = `<p>Voici les résultats d'une enquête sur ${ctx.theme} ${aupresDe(ctx)}.</p>${tableHtml}<p>Indique si chaque affirmation est vraie ou fausse.</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts('Vérifier des affirmations sur un tableau de données'));
    payload._meta.generatorKey = 'lecture_tableau_tableau_vf';
    return payload;
};

// ─── Format : texte_trou_drag ───────────────────────────────

// Texte à trous — modèles centrés sur la lecture du tableau (modèles 2, 3, 4).
const genTrouDragTableau = () => {
    const table = genTable();
    const { ctx, cells, rowTotals, colTotals, grandTotal } = table;
    const r = randInt(0, ctx.rows.length - 1);
    const c = randInt(0, ctx.cols.length - 1);
    const modele = randomChoice([2, 3, 4]);

    if (modele === 2) {
        // Remplacement (revue 2026-08-07) : « {{4}} lignes et {{2}} colonnes »
        // avait des réponses constantes (tableau toujours 4×2, mémorisable dès
        // le 2ᵉ passage) et un compte de colonnes contestable à l'écran.
        // Lecture réelle : la modalité la plus fréquente de chaque colonne,
        // re-tirage tant qu'un maximum de colonne n'est pas unique.
        const maxUnique = (tb, cc) => {
            const vals = tb.cells.map(row => row[cc]);
            const mx = Math.max(...vals);
            return vals.filter(v => v === mx).length === 1 ? tb.ctx.rows[vals.indexOf(mx)] : null;
        };
        let t = table;
        let max0 = maxUnique(t, 0);
        let max1 = maxUnique(t, 1);
        for (let tries = 0; (max0 === null || max1 === null) && tries < 50; tries++) {
            t = genTable();
            max0 = maxUnique(t, 0);
            max1 = maxUnique(t, 1);
        }
        if (max0 === null || max1 === null) return genTrouDragTableau();
        const c2 = t.ctx;
        const tableHtml = buildTableHtml(t);
        const phrase = c2.cellule
            ? `Le matin, le goûter le plus consommé est {{${max0}}}. L'après-midi, c'est {{${max1}}}. Le total général est {{${t.grandTotal}}}.`
            : `Chez les ${c2.cols[0]}, la réponse la plus fréquente est {{${max0}}}. Chez les ${c2.cols[1]}, c'est {{${max1}}}. Le total général est {{${t.grandTotal}}}.`;
        const autresModalites = c2.rows.filter(nom => nom !== max0 && nom !== max1);
        const distracteurs = filtrerDistracteurs(phrase, [
            randomChoice(autresModalites),
            String(t.colTotals[randInt(0, 1)]),
            String(t.grandTotal + randInt(1, 5)),
        ]);
        const texte = `<p>Voici les résultats d'une enquête sur ${c2.theme}.</p>${tableHtml}<p>Complète les phrases en glissant les valeurs proposées.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts('Lire et compléter un tableau à double entrée'), mode: 'drag', distracteurs });
        payload._meta.generatorKey = 'lecture_tableau_texte_trou_drag';
        return payload;
    } else if (modele === 3) {
        const tableHtml = buildTableHtml(table);
        const phrase = `Pour trouver le nombre ${deCelluleTxt(ctx, r, c)}, on lit au croisement de la {{ligne}} ${ctx.rows[r]} et de la {{colonne}} ${ctx.cols[c]}. On trouve {{${cells[r][c]}}}.`;
        const distracteurs = filtrerDistracteurs(phrase, ['case', 'total', String(rowTotals[r])]);
        const texte = `<p>Voici les résultats d'une enquête sur ${ctx.theme}.</p>${tableHtml}<p>Complète la phrase en glissant les mots proposés.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts('Lire et compléter un tableau à double entrée'), mode: 'drag', distracteurs });
        payload._meta.generatorKey = 'lecture_tableau_texte_trou_drag';
        return payload;
    } else {
        // modele 4 : Totaux
        const tableHtml = buildTableHtml(table);
        const phrase = `Le total de la ligne ${ctx.rows[r]} est {{${rowTotals[r]}}}. Le total de la colonne ${ctx.cols[c]} est {{${colTotals[c]}}}. Le total général est {{${grandTotal}}}.`;
        const distracteurs = filtrerDistracteurs(phrase, [String(grandTotal + randInt(1, 4)), String(rowTotals[(r + 1) % ctx.rows.length]), String(colTotals[(c + 1) % ctx.cols.length])]);
        const texte = `<p>Voici les résultats d'une enquête sur ${ctx.theme}.</p>${tableHtml}<p>Complète les phrases avec les bonnes valeurs.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts('Lire et compléter un tableau à double entrée'), mode: 'drag', distracteurs });
        payload._meta.generatorKey = 'lecture_tableau_texte_trou_drag';
        return payload;
    }
};

// Texte à trous — modèles centrés sur le vocabulaire / méthodologie (modèles 1, 5).
const genTrouDragVocabulaire = () => {
    const table = genTable();
    const { ctx, cells } = table;
    const r = randInt(0, ctx.rows.length - 1);
    const c = randInt(0, ctx.cols.length - 1);
    const modele = randomChoice([1, 5]);

    if (modele === 1) {
        const tableHtml = buildTableHtml(table);
        const phrase = `Dans cette enquête, la {{population}} étudiée est ${ctx.population}. Le {{caractère}} observé est ${ctx.caractere}. Par exemple, il y a {{${cells[r][c]}}} ${celluleTxt(ctx, r, c)}.`;
        // « tableau » et non « effectif » (revue 2026-08-07) : « la effectif /
        // Le effectif » s'éliminait par la grammaire seule.
        const distracteurs = filtrerDistracteurs(phrase, ['tableau', 'modalité', String(cells[r][c] + randInt(1, 3))]);
        const texte = `<p>Voici les résultats d'une enquête sur ${ctx.theme}.</p>${tableHtml}<p>Complète les phrases en glissant les mots proposés.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts('Compléter un texte sur la méthodologie d\'enquête'), mode: 'drag', distracteurs });
        payload._meta.generatorKey = 'vocabulaire_enquete_texte_trou_drag';
        return payload;
    } else {
        // modele 5 : modalités et effectif. UN SEUL trou modalité (revue
        // 2026-08-06, A1 constaté par l'utilisateur) : avec deux trous
        // « par exemple X et Y », l'ordre était interchangeable ET le
        // distracteur « autre ligne » était une modalité valide comptée
        // fausse. Les distracteurs ne sont plus jamais des modalités.
        const tableHtml = buildTableHtml(table);
        const phrase = `Une modalité du caractère est par exemple {{${ctx.rows[r]}}}. L'effectif ${deCelluleTxt(ctx, r, c)} est {{${cells[r][c]}}}.`;
        const distracteurs = filtrerDistracteurs(phrase, [ctx.cols[c], 'fréquence', String(cells[(r + 1) % ctx.rows.length][c])]);
        const texte = `<p>Voici les résultats d'une enquête sur ${ctx.theme}.</p>${tableHtml}<p>Complète les phrases en glissant les mots proposés.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts('Compléter un texte sur la méthodologie d\'enquête'), mode: 'drag', distracteurs });
        payload._meta.generatorKey = 'vocabulaire_enquete_texte_trou_drag';
        return payload;
    }
};

// ─── Registry & Export ───────────────────────────────────────
const GENERATORS = {
    // Compétence A — Lire / compléter le tableau
    lecture_tableau_qcm_unique:      genQcmTableau,
    lecture_tableau_texte:           genSaisie,
    lecture_tableau_tableau_vf:      genVF,
    lecture_tableau_texte_trou_drag: genTrouDragTableau,
    // Compétence B — Vocabulaire / méthodologie
    vocabulaire_enquete_qcm_unique:      genQcmVocabulaire,
    vocabulaire_enquete_relier:          genRelier,
    vocabulaire_enquete_texte_trou_drag: genTrouDragVocabulaire,
};

export const CATEGORIES = [
    {
        id: 'lecture_tableau',
        label: 'Lire un tableau à double entrée',
        formatsList: [
            { key: 'lecture_tableau_qcm_unique',      label: 'QCM' },
            { key: 'lecture_tableau_texte',           label: 'Saisie' },
            { key: 'lecture_tableau_tableau_vf',      label: 'V/F' },
            { key: 'lecture_tableau_texte_trou_drag', label: 'Trous' },
        ],
    },
    {
        id: 'vocabulaire_enquete',
        label: 'Vocabulaire statistique et méthodologie',
        formatsList: [
            { key: 'vocabulaire_enquete_qcm_unique',      label: 'QCM' },
            { key: 'vocabulaire_enquete_relier',          label: 'Relier' },
            { key: 'vocabulaire_enquete_texte_trou_drag', label: 'Trous' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};
